import { useState } from 'react';
import { useQuery } from 'react-query';

import { TABLET_API_ORIGIN } from '@/global/env';
import { errorHandler } from '@/lib/ErrorHandler';
import { getClient } from '@/lib/restClient';
import { CamelOrderCouponUpdateItemParams } from '@/pages/camel-order/coupons/create/useCamelOrderCouponItemForm';
import {
  ACTIVE,
  DELETED,
  INACTIVE,
} from '@/pages/camel-order/coupons/index/components/CouponTable/CouponTable';
import { CamelOrderCouponService } from '@/services/CamelOrder/CamelOrderCouponService';
import { SiteListResponse } from '@/services/Site/hooks/list/useSiteList';

import { Coupon, Site } from './types';

const camelOrderCouponService = new CamelOrderCouponService(getClient());

const camelOrderCouponDelete = async (id: string): Promise<void> => {
  await camelOrderCouponService.delete(id);
  alert('クーポンを削除しました');
};

const camelOrderCouponUpdate = async (
  id: string,
  params: CamelOrderCouponUpdateItemParams
): Promise<void> => {
  await camelOrderCouponService.update(id, params);
  alert('クーポンステータスを更新しました');
};

export const useCamelOrderCoupons = () => {
  const [targetSite, setTargetSite] = useState<Site | null>(null);
  const changeUrlHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const site = sites?.find((s) => s.id === e.target.value);
    setTargetSite(site || null);
  };

  const {
    data: sites,
    error: sitesError,
    isLoading: isSitesLoading,
  } = useQuery<Site[], Error>(
    'listSite',
    () => {
      return getClient()
        .get<SiteListResponse[]>(
          `${TABLET_API_ORIGIN}/dashboard-api/sites` as const
        )
        .then((sites) =>
          sites.map((s) => ({
            id: s.id,
            url: s.url,
          }))
        )
        .then((sites) => {
          if (sites.length > 0) {
            setTargetSite(sites[0]);
          }
          return sites;
        })
        .catch((error: Error) => errorHandler({ error }));
    },
    { cacheTime: 0 }
  );
  const {
    data: coupons,
    error: couponsError,
    isLoading: isCouponsLoading,
    refetch: refetchCoupons,
  } = useQuery<Coupon[], Error>(
    ['listCoupon', targetSite?.id],
    () => {
      if (targetSite === null) {
        return [];
      }
      return getClient()
        .get<Coupon[]>(
          `${TABLET_API_ORIGIN}/dashboard-api/coupons?site_id=${targetSite.id}` as const
        )
        .catch((error: Error) => errorHandler({ error }));
    },
    { enabled: !!targetSite, cacheTime: 0 }
  );
  const handleStatusChange = async (id: string, value: string) => {
    if (value === DELETED) {
      if (confirm('削除しますか？')) {
        await camelOrderCouponDelete(id);
      }
    } else if (value === ACTIVE || value === INACTIVE) {
      await camelOrderCouponUpdate(id, {
        status: value,
      });
    }

    refetchCoupons();
  };
  return {
    coupons: coupons ?? [],
    sites: sites ?? [],
    isLoading: isCouponsLoading || isSitesLoading,
    targetSite,
    changeUrlHandler,
    handleStatusChange,
    error: couponsError || sitesError,
  };
};
